import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, Menu, ShoppingBasket, User, LogOut, LayoutDashboard } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { CartDrawer } from "@/components/CartDrawer";

const LINKS = [
  { to: "/tienda", label: "Tienda" },
  { to: "/historia", label: "Nuestra historia" },
  { to: "/mayoreo", label: "Mayoreo" },
  { to: "/faq", label: "FAQ" },
  { to: "/contacto", label: "Contacto" },
];

export function Navbar() {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-40 border-b border-sand bg-cream/90 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-8">
        <Link to="/" className="flex items-center" data-testid="nav-logo" aria-label="SoyDiVa — ir al inicio">
          <BrandLogo />
        </Link>

        <nav className="hidden items-center gap-6 lg:flex" aria-label="Principal">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-semibold transition-colors hover:text-habanero ${
                  isActive ? "text-habanero" : "text-cocoa"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {user && user.role === "admin" && (
            <Button
              variant="ghost"
              size="icon"
              data-testid="nav-admin-button"
              aria-label="Panel de administración"
              onClick={() => navigate("/admin")}
            >
              <LayoutDashboard size={20} />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            data-testid="nav-account-button"
            aria-label="Mi cuenta"
            onClick={() => navigate("/cuenta")}
          >
            <User size={20} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            data-testid="nav-cart-button"
            aria-label="Abrir carrito"
            onClick={() => setOpen(true)}
          >
            <ShoppingBasket size={20} />
            {count > 0 && (
              <motion.span
                key={count}
                initial={{ scale: 1.8 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 500, damping: 15 }}
                data-testid="cart-count-badge"
                className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-habanero px-1 text-[11px] font-bold text-white"
              >
                {count}
              </motion.span>
            )}
          </Button>
          {user && (
            <Button
              variant="ghost"
              size="icon"
              data-testid="nav-logout-button"
              aria-label="Cerrar sesión"
              onClick={logout}
            >
              <LogOut size={18} />
            </Button>
          )}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="nav-menu-button" aria-label="Menú">
                <Menu size={22} />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-cream">
              <SheetHeader>
                <SheetTitle className="font-script text-4xl text-cocoa">SoyDiVa</SheetTitle>
              </SheetHeader>
              <nav className="mt-8 flex flex-col gap-4" aria-label="Menú móvil">
                {LINKS.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="font-display text-2xl font-semibold text-cocoa transition-colors hover:text-habanero"
                  >
                    {l.label}
                  </NavLink>
                ))}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <CartDrawer />
    </header>
  );
}
